<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import Mousetrap from "mousetrap";
import { VList } from "virtua/vue";
import { throttle } from "underscore";
import { useRouter } from "vue-router";
import { computed, nextTick, onMounted, provide, ref, shallowRef, watch } from "vue";
import { ToolbarButton, ToolbarRoot, ToolbarToggleGroup, ToolbarToggleItem } from "reka-ui";

import { VIEWER_PAGE_PADDING, PANEL_SIZE } from "../constants";

import RsLoading from "@/components/Loading.vue";
import ConfirmPopover from "@/components/ConfirmPopover.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import RsViewerPanelViews from "./Panels/ViewerPanelViews.vue";
import RsViewerPanelSearch from "./Panels/ViewerPanelSearch.vue";
import RsViewerPageRenderer from "./ViewerPageRenderer.vue";
import { groupTextLines } from "./group-text-lines";
import RsViewerPanelAnnotations from "./Panels/ViewerPanelAnnotations.vue";
import RsViewerPanelRelated from "@/components/CasePanels/ViewerTranscriptPanelRelated.vue";
import SummaryThemesLegend from "@/components/CasePanels/RecordView/SummaryThemesLegend.vue";
import ConfirmAnnotationDelete from "@/components/ConfirmAnnotationDelete.vue";

import { caseFileService } from "@/services";
import { isRedaction } from "@/services/AnnotationService";
import { getSvgDimensions } from "@/services/CaseFileService";
import { $can, getAnnotationMeta, isHighlightAnnotation, isRectangleAnnotation } from "@/services/UtilitiesService";

import { useBrandStore } from "@/stores/brand-store";
import { useMessageStore } from "@/stores/message-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useSettingsStore } from "@/stores/settings-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";

import { useWindow } from "@/hooks/use-window";
import { useIsPopout } from "@/hooks/use-is-popout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";
import { useDirectlyRelatedFiles } from "@/hooks/use-directly-related-files";

import { Panels } from "@/models/PanelEnums";

import type { Metadata } from "@/models/Metadata";
import type { MarkBase } from "@/models/MarkBase";
import type { PageData } from "@/models/PageData";
import type { ViewingSession } from "@/models/ViewingSession";
import type { HighlightCustomArea, PageStatus, Scale, SearchResult, TextLine, ViewerPage } from "@/models/Viewer";

import type { ParsedMark } from "@/models/Mark";
import type { DockviewPanelComponentEmits } from "@/hooks/use-case-panel";
import type { ViewerLoadingState } from "./FileTypeDocument.vue";
import type { TranscriptAnnotation, HighlightAnnotation, RectangleAnnotation } from "@/models/Annotation";

type LastViewerAction =
  | "INIT"
  | "PAGE_CHANGED"
  | "ACTIVE_ITEM_CHANGED"
  | "PANEL_TOGGLED"
  | "SEARCH_SELECTED"
  | "SCROLL";
export type MouseTool =
  | "PanAndEdit"
  | "RectangleAnnotation"
  | "HighlightAnnotation"
  | "SelectText"
  | "THUMBNAILS"
  | "RectangleRedaction"
  | "TextSelectionRedaction";

const emit = defineEmits<
  {
    redactionCommitted: [];
    viewerLoaded: [];
    reloadAnnotations: [];
    fileLoadError: [error?: any];
    attachmentSelected: [fileReference: string];
    switchOnDemand: [];
    startPreConvert: [];
    switchViewingPackage: [];
  } & DockviewPanelComponentEmits
>();

withDefaults(defineProps<{
  isVisible: boolean;
  session: ViewingSession;
  snippets?: MarkBase[];
  viewerScale: Scale;
  file: Metadata;
  selectedArea?: HighlightCustomArea;
  retryCount?: number;
  fileLoadingState: ViewerLoadingState;
}>(), { retryCount: 0, })

const $router = useRouter();
const window = useWindow();
const isPopout = useIsPopout();
const routeParams = useCaseRouteParams();
const relatedFiles = useDirectlyRelatedFiles(__props.file);

// By default all panels are hidden
const currentPanel = ref<Record<string, boolean>>({});
const viewerRef = ref<InstanceType<typeof VList>>();

const ignorePageParamChange = ref(false);

const rotation = ref<number[]>([]);
provide("rotation", rotation);

function rotatePage(i: number) {
  const isNumber = Object.prototype.toString.call(rotation.value[i]).slice(8, -1) === "Number";

  rotation.value[i] = isNumber ? rotation.value[i] + 90 : 90;
  if (rotation.value[i] === 360) rotation.value[i] = 0;
}

function rotateAllPages() {
  for (let i = 1; i <= totalPages.value; i++) {
    rotatePage(i);
  }
}

function rotateCurrentPage() {
  rotatePage(activePage.value);
}

const isFullScreen = ref(isPopout);
const totalPages = ref(1);

const currentMouseTool = ref<MouseTool>("PanAndEdit");
let panState = _ref<"IDLE" | "PANNING">("IDLE");
let panOffset = _ref<{
  x: number;
  y: number;
  top: number;
  left: number;
} | null>(null);
const pages = shallowRef<Array<Omit<ViewerPage, "dimensions"> & { dimensions?: ViewerPage["dimensions"] }>>([]);
const vScroll = ref({ scrollOffset: 0, startIndex: 0, endIndex: 0 });
const lastViewerAction = ref<LastViewerAction>("INIT");
let scrollCounter = _ref(0);
const selectedSearch = ref<SearchResult>();
const searchResults = ref<SearchResult[]>([]);

const scale = ref(100);
const fitToScreen = computed(() => scale.value === 100);

const hasScrolledToActiveItem = ref(false);

const activePage = computed(() => {
  const exactPage = Math.max(
    1,
    Math.min(totalPages.value, 1 + Math.floor((vScroll.value.startIndex + vScroll.value.endIndex) / 2)),
  );

  // Not on the last page
  if (exactPage < totalPages.value && viewerRef.value != null) {
    const pageOffset = viewerRef.value.getItemOffset(exactPage - 1);
    const nextPageOffset = viewerRef.value.getItemOffset(exactPage);

    // Previous Page start - Next Page start gives height of a page
    const pageHeight = nextPageOffset - pageOffset;

    // amount of page to have been scrolled before next page is active
    const threshold = 0.85;

    if (vScroll.value.scrollOffset > pageOffset + pageHeight * threshold) {
      return exactPage + 1;
    }
  }

  return exactPage;
});

const activeItem = computed(() => {
  if (__props.selectedArea != null) return { page: __props.selectedArea.page, rect: __props.selectedArea.rectangle };

  if (selectedSearch.value != null) {
    return {
      page: { ...selectedSearch.value.pageData, number: selectedSearch.value.pageIndex + 1 },
      rect: selectedSearch.value.boundingRectangle,
    };
  }

  if (selectedAnnotation.value != null) {
    const isRectangleHighlightRedaction = (
      a: RectangleAnnotation | HighlightAnnotation | TranscriptAnnotation,
    ): a is RectangleAnnotation | HighlightAnnotation =>
      "type" in a && (a.type === "TextSelectionRedaction" || a.type === "RectangleRedaction");

    if (
      isRectangleAnnotation(selectedAnnotation.value) ||
      isHighlightAnnotation(selectedAnnotation.value) ||
      isRectangleHighlightRedaction(selectedAnnotation.value)
    ) {
      const { rect, page } = getAnnotationMeta(selectedAnnotation.value);
      return { page: { ...page, number: selectedAnnotation.value.pageNumber }, rect };
    }
  }

  return undefined;
});

const currentPanelGlobal = computed(() => {
  const { viewer, shared } = useCaseFileStore().currentPanelGlobal;
  return { ...viewer, ...shared };
});

const annotations = _computed(() => {
  return useAnnotationStore().annotations.filter(
    (a): a is ParsedMark<RectangleAnnotation | HighlightAnnotation> =>
      a.fileReference === __props.file.fileReference &&
      (isRectangleAnnotation(a.parsedMarkData) || isHighlightAnnotation(a.parsedMarkData)),
  );
});

const canAnnotate = _computed(() => {
  const fileMeta = useMetadataStore().filesByRef[__props.file.fileReference];
  const classification: string = fileMeta?.aiClassification || "unknown";

  const isUnknown: boolean = /unknown/i.test(classification);
  const allowForUnknown: boolean =
    useBrandStore().viewerConfig.evidenceThemes?.enableForUnknownClassifications || false;

  return $can("annotate") && (!isUnknown || allowForUnknown);
});

const hasRedaction = _computed(() => {
  return annotations.value.find((a) => isRedaction(a.parsedMarkData)) != null;
});

const canRedact = _computed(() => {
  return useUserPermissionStore().userPermission.canRedact;
});

const canPreConvert = _computed(() => {
  return useUserPermissionStore().userPermission.canPreConvert ?? false;
});

const isPreConvertPending = _computed(() => {
  return (useCaseFileStore().currentFile || { edxPreConvertStatus: undefined }).edxPreConvertStatus === "pending";
});

const selectedAnnotation = computed(() => {
  const { annotation, casefile } = routeParams.value;
  if (annotation == null || casefile !== __props.file.fileReference) return;

  return annotations.value.find((a) => a.markId === annotation)?.parsedMarkData;
});

const viewerPanels = computed(() => {
  type EmitsToProps<T extends Record<string, (...args: any) => any>> = keyof T extends `on${infer C}`
    ? { [K in Uncapitalize<C>]: T[`on${Capitalize<K>}`] | undefined }
    : never;

  interface ViewerPanel<C extends abstract new (...args: any) => any> {
    key: string;
    show: boolean;
    icon: string;
    title: string;
    showPanel: boolean;
    panelComponent: C;
    panelComponentProps: InstanceType<C>["$props"];
    panelComponentEmits: EmitsToProps<Pick<InstanceType<C>, Extract<keyof InstanceType<C>, `on${string}`>>>;
  }

  function showPanel(key: string) {
    return currentPanel.value[key] || currentPanelGlobal.value[key];
  }

  function addPanel<C extends abstract new (...args: any) => any>(panel: ViewerPanel<C>): ViewerPanel<C> {
    return panel;
  }

  return [
    addPanel({
      key: Panels.shared.SEARCH,
      show: true,
      icon: "fas fa-search",
      title: "Search",
      panelComponent: RsViewerPanelSearch,
      panelComponentProps: {
        session: __props.session.viewingSessionId,
        searchResults: searchResults.value,
        selectedSearch: selectedSearch.value,
        predefinedSearch: useSettingsStore().predefinedSearch,
      },
      panelComponentEmits: {
        selectSearch,
        search: (results) => (searchResults.value = results),
      },
      get showPanel(): boolean {
        return this.show && showPanel(this.key);
      },
    }),
    addPanel({
      key: Panels.viewer.VIEWS,
      show: true,
      icon: "fas fa-th-large",
      title: "Thumbnails",
      panelComponent: RsViewerPanelViews,
      panelComponentProps: {
        session: __props.session.viewingSessionId,
        fileReference: __props.file.fileReference,
        totalPages: totalPages.value,
        searchResults: searchResults.value,
      },
      panelComponentEmits: {},
      get showPanel(): boolean {
        return this.show && showPanel(this.key);
      },
    }),
    addPanel({
      key: Panels.shared.RELATED,
      show:
        (__props.file.parent != null ||
          relatedFiles.value.unfilteredFiles.children.length > 0 ||
          relatedFiles.value.unfilteredFiles.siblings.length > 0) &&
        !isPopout,
      icon: "fas fa-file-lines",
      title: "Related",
      panelComponent: RsViewerPanelRelated,
      panelComponentProps: { parentFileReference: __props.file.parent, attachments: relatedFiles.value },
      panelComponentEmits: {},
      get showPanel(): boolean {
        return this.show && showPanel(this.key);
      },
    }),
    addPanel({
      key: Panels.shared.ANNOTATIONS,
      show: !isPopout,
      icon: "fas fa-pen",
      title: "Marks",
      panelComponent: RsViewerPanelAnnotations,
      panelComponentProps: { file: __props.file, snippets: __props.snippets ?? [] },
      panelComponentEmits: {
        reloadAnnotations: () => emit("reloadAnnotations"),
      },
      get showPanel(): boolean {
        return this.show && showPanel(this.key);
      },
    }),
  ];
});

const viewerScaleWithoutPanels = computed(() => {
  const openPanelCount = viewerPanels.value.filter((p) => p.showPanel).length;
  if (openPanelCount < 1) return __props.viewerScale;

  return {
    width: Math.max(VIEWER_PAGE_PADDING * 2, __props.viewerScale.width - PANEL_SIZE * openPanelCount),
    height: __props.viewerScale.height,
  };
});

watch(activeItem, () => {
  if (activeItem.value == null) return;
  hasScrolledToActiveItem.value = false;
});

watch([() => __props.isVisible, pages, activeItem, hasScrolledToActiveItem], ([newIsVisible]) => {
  if (!newIsVisible || hasScrolledToActiveItem.value || activeItem.value == null) return;

  lastViewerAction.value = "ACTIVE_ITEM_CHANGED";

  const pageNumber = activeItem.value.page.number;
  if (pageNumber == null || pageNumber > pages.value.length) return;

  if (pages.value[pageNumber - 1].status === "SUCCESS") hasScrolledToActiveItem.value = true;

  nextTick(() => scrollToActiveItem(1));
});

watch(
  () => routeParams.value.filepage,
  () => {
    if (__props.file.fileReference !== routeParams.value.casefile) return;

    // The only time the param change should not be ignored is when switching with a popout
    if (ignorePageParamChange.value) {
      ignorePageParamChange.value = false;
      return;
    }

    const filepage = routeParams.value.filepage;
    if (filepage == null) return;

    // Reset the last viewer action to INIT so if the page isn't in the "SUCCESS" load state yet,
    // all other pages in that region would do so as needed.
    lastViewerAction.value = "INIT";
    nextTick(() => viewerRef.value?.scrollToIndex(+filepage - 1));
  },
);

onMounted(() => {
  initializeSvgRenderer();

  Mousetrap.bind("esc", () => {
    if (routeParams.value.annotation == null || isPopout) return;

    const { brand, casename, casefile } = routeParams.value;
    if (__props.file.fileReference !== casefile) return;

    // Manually changing page param, should be ignored by watcher
    ignorePageParamChange.value = true;

    $router.push({
      name: "casefilepage",
      params: { brand, casename, casefile, filepage: activePage.value },
    });
  });
});

function changeScale(type: "INCREASE" | "DECREASE" | "FIT") {
  if (type === "INCREASE") {
    scale.value = Math.min(205, scale.value + 15);
  } else if (type === "DECREASE") {
    scale.value = Math.max(40, scale.value - 15);
  } else if (type === "FIT") {
    scale.value = 100;
  }
}

function changePage(pageNumber: number) {
  const { brand, casename, casefile, filepage = -1 } = routeParams.value;
  if (
    // The filepage is constant for a popout, so we won't need this condition for one
    (!isPopout && +filepage === pageNumber) ||
    __props.file.fileReference !== casefile
  )
    return;

  if (activePage.value !== pageNumber) {
    lastViewerAction.value = "PAGE_CHANGED";
    viewerRef.value?.scrollToIndex(pageNumber - 1);
  }

  if (isPopout) return;

  // Manually changing page param, should be ignored by watcher
  ignorePageParamChange.value = true;

  $router.push({
    name: "casefilepage",
    params: { brand, casename, casefile, filepage: Math.max(1, pageNumber) },
  });
}

function toggleFullScreen() {
  isFullScreen.value = !isFullScreen.value;
}

function togglePanel(panel: string) {
  if (currentPanel.value[panel]) {
    currentPanel.value[panel] = false;
    useCaseFileStore().currentViewerPanelGlobalChanged(panel, true);
  } else if (!currentPanel.value[panel]) {
    currentPanel.value[panel] = !currentPanelGlobal.value[panel];
    useCaseFileStore().currentViewerPanelGlobalChanged(panel, false);
  }

  // Wait for vScroll sizes to update
  setTimeout(() => {
    lastViewerAction.value = "PANEL_TOGGLED";
    scrollToActiveItem();
  }, 100);
}

async function commitRedaction() {
  if (isPopout) return;

  try {
    if (selectedAnnotation.value != null && isRedaction(selectedAnnotation.value)) {
      $router.push({
        name: "casefile",
        params: {
          brand: routeParams.value.brand,
          casename: routeParams.value.casename,
          casefile: __props.file.fileReference,
        },
      });
    }

    await useAnnotationStore().redact({ fileReference: __props.file.fileReference });
    emit("redactionCommitted");
  } catch {}
}

function panStart(evt: MouseEvent) {
  if (currentMouseTool.value !== "PanAndEdit") return;
  const viewer = viewerRef.value!.$el;
  panOffset.value = {
    x: evt.clientX,
    y: evt.clientY,
    top: viewer.scrollTop,
    left: viewer.scrollLeft,
  };
  panState.value = "PANNING";
}

function pan(evt: MouseEvent) {
  if (currentMouseTool.value !== "PanAndEdit" || panState.value === "IDLE") return;
  const dx = evt.clientX - panOffset.value!.x;
  const dy = evt.clientY - panOffset.value!.y;

  viewerRef.value!.$el.scrollTop = panOffset.value!.top - dy;
  viewerRef.value!.$el.scrollLeft = panOffset.value!.left - dx;
}

function panEnd() {
  panState.value = "IDLE";
}

function pageUpdated(payload: {
  pageNumber: number;
  svg?: string;
  dimensions?: Scale;
  status?: PageStatus;
  textLines?: TextLine[];
}) {
  pages.value = pages.value.map((page, idx) => {
    if (idx !== payload.pageNumber - 1) return page;
    return { ...page, ...payload };
  });

  if (payload.status !== "SUCCESS") return;

  if (lastViewerAction.value === "INIT") {
    if (activeItem.value != null) scrollToActiveItem();
    else if (routeParams.value.filepage != null) viewerRef.value?.scrollToIndex(+routeParams.value.filepage - 1);
  } else if (lastViewerAction.value === "ACTIVE_ITEM_CHANGED" && activeItem.value != null) {
    scrollToActiveItem();
  }
}

async function loadPage(pageNumber: number) {
  if (pages.value[pageNumber - 1].status === "LOADING") return;

  try {
    pageUpdated({ pageNumber, status: "LOADING" });

    const [svgString, text] = await Promise.all([
      caseFileService.getDocumentSVG(__props.session.viewingSessionId, pageNumber - 1),
      caseFileService.getDocumentText(__props.session.viewingSessionId, pageNumber - 1),
    ]);

    // IDs returned are same for every document
    // Make them unique so same page won't be repeated everywhere.
    // Spent whole day figuring this out. oof.
    const svg = svgString
      .replaceAll(/id="([a-zA-Z][\w:.-]*)"/g, `id="${__props.file.fileReference + pageNumber}$1"`)
      .replaceAll(/xlink:href="#([a-zA-Z][\w:.-]*)"/g, `xlink:href="#${__props.file.fileReference + pageNumber}$1"`);

    const dimensions = getSvgDimensions(svg);

    const page = text.pages[0];
    const textLines = groupTextLines(page.text, page.rectangles);

    pageUpdated({ pageNumber, svg, textLines, dimensions, status: "SUCCESS" });
  } catch (e) {
    pageUpdated({ pageNumber, status: "ERROR" });
  }
}

function onScroll(scrollOffset: number) {
  vScroll.value.scrollOffset = scrollOffset;
  onRangeChange();

  scrollCounter.value += 1;
  pageScrolled();

  if (isPopout || lastViewerAction.value !== "SCROLL") return;

  const { brand, casename, casefile, filepage = 1 } = routeParams.value;
  if (activePage.value === +filepage || __props.file.fileReference !== casefile) return;

  if (
    selectedAnnotation.value != null &&
    "pageNumber" in selectedAnnotation.value &&
    selectedAnnotation.value.pageNumber === activePage.value
  ) {
    return;
  }

  // Manually changing page param, should be ignored by watcher
  ignorePageParamChange.value = true;

  $router.push({
    name: "casefilepage",
    params: { brand, casename, casefile, filepage: activePage.value },
  });
}

const onRangeChange = throttle(() => {
  if (viewerRef.value == null) return;

  vScroll.value.startIndex = viewerRef.value.findStartIndex();
  vScroll.value.endIndex = viewerRef.value.findEndIndex();
}, 150);

const pageScrolled = throttle(
  () => {
    // Page scroll can happen in 2 ways
    // 1. programmatically like using scrollTop variable
    // 2. User scrolling using mouse or touch screen
    // We need to update the page number in the url only when user scrolls through mouse
    // This little hack is identify that. There will more than 10 events fired within few milliseconds when user scrolls with mouse.
    if (scrollCounter.value < 5) {
      scrollCounter.value = 0;
      return;
    }

    scrollCounter.value = 0;
    lastViewerAction.value = "SCROLL";
  },
  150,
  { leading: false },
);

function scrollToActiveItem(retry = 0) {
  // User is scrolling through pages avoid un-necessary page jumping
  if (lastViewerAction.value === "SCROLL" || viewerRef.value == null || activeItem.value == null) return;

  try {
    const { rect, page: rectPage } = activeItem.value;
    if (rectPage.number > pages.value.length) throw new Error("Page number exceeds bounds");

    const page = pages.value[rectPage.number - 1].dimensions ?? viewerScaleWithoutPanels.value;

    const normalizedRectX = page.width * (rect.x / rectPage.width);
    const normalizedRectY = page.height * (rect.y / rectPage.height);

    let y = 0;
    switch (rotation.value[rectPage.number]) {
      case 90:
        y = page.width - (normalizedRectX + rect.width);
        break;
      case 180:
        y = page.height - (normalizedRectY + rect.height);
        break;
      case 270:
        y = normalizedRectX;
        break;
      default:
        y = normalizedRectY;
        break;
    }

    y = y * getPageScale(rectPage.number) * getAnnotationScale(rectPage.number, page);

    // A max of 3 retries in any case
    retry = Math.min(retry, 3);

    do {
      // Perform the scroll
      viewerRef.value.scrollToIndex(rectPage.number - 1, { offset: y });
    } while (
      // Retry the scroll if within the max amount of times and the scrolled offset is within the threshold
      // from the expected scroll offset
      --retry > 0 &&
      Math.abs(viewerRef.value.scrollOffset - Math.round(viewerRef.value.getItemOffset(rectPage.number - 1) + y)) > 50
    );
  } catch (error) {
    console.error(error);
    useMessageStore().updateMessage({
      title: "Error",
      message: "Could not scroll to active item",
      type: "error",
      showReload: false,
      autoHide: true,
    });
  }
}

function selectSearch(searchResult: SearchResult) {
  if (routeParams.value.casefile == null) return;

  selectedSearch.value = searchResult;
  if (!searchResult) return;

  changePage(searchResult.pageIndex + 1);
  lastViewerAction.value = "SEARCH_SELECTED";
  scrollToActiveItem();
}

async function initializeSvgRenderer() {
  try {
    const attributes = await caseFileService.getDocumentAttributes(__props.session.viewingSessionId);
    totalPages.value = attributes.pageCount;

    pages.value = Array.from({ length: totalPages.value }).map((_, idx) => ({
      pageNumber: idx + 1,
      svg: "",
      textLines: [],
      dimensions: undefined,
      status: "INIT",
    }));

    nextTick(() => {
      if (routeParams.value.filepage == null || lastViewerAction.value !== "INIT") return;
      viewerRef.value?.scrollToIndex(+routeParams.value.filepage - 1);
    });

    emit("viewerLoaded");
  } catch (e) {
    emit("fileLoadError", e);
  }
}

function getFitToScreenScale(pageNumber: number) {
  if (pageNumber > pages.value.length || viewerScaleWithoutPanels.value.width <= VIEWER_PAGE_PADDING) return 1;

  const page = pages.value[pageNumber - 1];
  if (!page.svg) return 1;

  return [90, 270].includes(rotation.value[pageNumber])
    ? (viewerScaleWithoutPanels.value.width - VIEWER_PAGE_PADDING) /
        (page.dimensions?.height ?? viewerScaleWithoutPanels.value.height)
    : (viewerScaleWithoutPanels.value.width - VIEWER_PAGE_PADDING) /
        (page.dimensions?.width ?? viewerScaleWithoutPanels.value.width);
}

function getPageScale(pageNumber: number) {
  const pageFitScale = getFitToScreenScale(pageNumber);

  if (fitToScreen.value) return pageFitScale;
  return (scale.value * pageFitScale) / 100;
}

function getAnnotationScale(pageNumber: number, pageData: PageData) {
  const page = pages.value[pageNumber - 1];
  return [90, 270].includes(rotation.value[pageNumber])
    ? (page.dimensions?.height ?? viewerScaleWithoutPanels.value.height) / pageData.height
    : (page.dimensions?.width ?? viewerScaleWithoutPanels.value.width) / pageData.width;
}

function panelPopoutAction() {
  emit("panelPopoutAction", { filepage: String(activePage.value) });
}
</script>
<template>
  <Teleport :disabled="!isFullScreen" :to="window.document.body">
    <div
      v-if="fileLoadingState !== 'LOADING'"
      class="tw-flex tw-size-full tw-min-w-0 tw-flex-col tw-flex-nowrap tw-bg-[#e7e9ef] tw-text-sm tw-text-[#555555]"
      :class="{ 'tw-fixed tw-inset-0 tw-z-[100]': isFullScreen }"
      :style="{ fontFamily: `'Helvetica Neue', Helvetica, Arial, sans-serif` }"
    >
      <ToolbarRoot
        class="custom-controls tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-start tw-gap-x-2 tw-gap-y-1 tw-p-2"
      >
        <div class="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1">
          <template v-for="k in viewerPanels" :key="k.key">
            <ToolbarButton
              v-if="k.show"
              :aria-label="`Open ${k.title} Panel`"
              class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-rounded-sm tw-border tw-border-transparent tw-px-1"
              :class="{
                'tw-bg-[#676767] tw-shadow': currentPanel[k.key],
                '!tw-border-gray-300': currentPanelGlobal[k.key],
              }"
              @click="() => togglePanel(k.key)"
            >
              <i aria-hidden="true" :class="k.icon" class="!tw-mx-0 tw-flex-shrink-0 !tw-px-0" />
              <span>{{ k.title }}</span>
            </ToolbarButton>
          </template>
          <ConfirmPopover
            v-if="canRedact && hasRedaction && !isPopout"
            confirm-text="Commit"
            @confirmed="commitRedaction()"
          >
            <template #trigger>
              <i aria-hidden="true" class="fas fa-eraser tw-flex-shrink-0"></i>
              <span> Commit Redactions</span>
            </template>
            <template #question>
              Are you sure? This will commit the redaction masks to the record. The original record uploaded will be
              permanently destroyed and replaced with a PDF of the redacted record. Access to this record will be
              temporarily suspended in Viewer whilst the redactions are being committed.
            </template>
          </ConfirmPopover>
          <ConfirmPopover
            v-if="!isPopout && session.requestedUsingOnDemand && !isPreConvertPending"
            confirm-text="Yes"
            @confirmed="$emit('switchViewingPackage')"
          >
            <template #trigger>
              <StringTooltip item="Switch to pre converted">
                <i class="fas fa-hammer tw-flex-shrink-0" />
              </StringTooltip>
            </template>
            <template #question>
              Are you sure you want to switch back to using the pre-converted viewing package?
            </template>
          </ConfirmPopover>
          <ConfirmPopover
            v-if="!isPopout && session.isViewingPackage"
            confirm-text="Yes"
            :trigger-props="{ 'aria-label': 'Having document rendering issues?' }"
            @confirmed="$emit('switchOnDemand')"
          >
            <template #trigger>
              <StringTooltip item="Having document rendering issues?">
                <i class="fas fa-hammer tw-flex-shrink-0" />
              </StringTooltip>
            </template>
            <template #question>
              <span>
                You are viewing this document using a pre-converted viewing package. If you think this viewing package
                is corrupt, you can request an on-demand conversion of this document.
              </span>
              <span class="tw-mb-2">Do you want to start an on-demand session?</span>
              <span class="tw-font-bold">
                NB. On-demand sessions are slower than viewing packages, care should be taken with large documents.
              </span>
            </template>
          </ConfirmPopover>
          <ConfirmPopover
            v-if="!isPopout && session.requestedUsingOnDemand && canPreConvert && !isPreConvertPending"
            confirm-text="Yes"
            @confirmed="$emit('startPreConvert')"
          >
            <template #trigger>
              <StringTooltip item="Pre-convert document">
                <i class="far fa-images tw-flex-shrink-0" />
              </StringTooltip>
            </template>
            <template #question>
              <span>You have indicated that this document had a corrupt pre-converted viewing package.</span>
              <span class="tw-mb-2">
                Do you want the system to pre-convert the document again to remove the rendering issues?
              </span>
              <span class="tw-font-bold">
                NB. A pre-conversion can take several minutes dependent on document size and pre-conversion
                availability. You can use the document while this process is ongoing.
              </span>
            </template>
          </ConfirmPopover>
        </div>
        <div class="tw-flex tw-shrink-0 tw-gap-2">
          <div v-if="isPopout" tabindex="0" class="tw-mr-3 tw-flex">
            {{ file.displayName }}
          </div>
          <div v-if="isPopout" class="options-container tw-flex">
            <StringTooltip item="Switch Viewer" #="{ item }">
              <ToolbarButton class="option" :aria-label="item" @click="panelPopoutAction">
                <i aria-hidden="true" class="fa fa-random" />
              </ToolbarButton>
            </StringTooltip>
          </div>
          <div v-if="!isPopout" class="options-container tw-flex">
            <StringTooltip item="Open in Pop-Out Window" #="{ item }">
              <ToolbarButton class="option" :aria-label="item" @click="panelPopoutAction">
                <i aria-hidden="true" class="fas fa-external-link-alt" />
              </ToolbarButton>
            </StringTooltip>
            <StringTooltip item="Full Screen" #="{ item }">
              <ToolbarButton
                class="option"
                :aria-label="item"
                :class="{ active: isFullScreen }"
                @click="toggleFullScreen"
              >
                <i
                  aria-hidden="true"
                  class="fas"
                  :class="isFullScreen ? 'fa-compress-arrows-alt' : 'fa-expand-arrows-alt'"
                />
              </ToolbarButton>
            </StringTooltip>
          </div>
          <ToolbarToggleGroup
            v-if="!isPopout"
            class="tw-flex tw-gap-2"
            aria-label="Current Mouse Tool"
            :model-value="currentMouseTool"
            @update:model-value="
              (tool) => {
                if (tool == null) return;
                currentMouseTool = tool as MouseTool;
              }
            "
          >
            <div class="options-container tw-flex">
              <StringTooltip item="Pan Tool" #="{ item }">
                <ToolbarToggleItem
                  value="PanAndEdit"
                  class="option"
                  :aria-label="item"
                  :class="{ active: currentMouseTool === 'PanAndEdit' }"
                >
                  <i class="far fa-hand-paper" />
                </ToolbarToggleItem>
              </StringTooltip>
              <StringTooltip item="Rectangle Mark Tool" #="{ item }">
                <ToolbarToggleItem
                  v-if="canAnnotate"
                  class="option"
                  value="RectangleAnnotation"
                  :aria-label="item"
                  :class="{ active: currentMouseTool === 'RectangleAnnotation' }"
                >
                  <i class="far fa-square" />
                </ToolbarToggleItem>
              </StringTooltip>
              <StringTooltip item="Text Mark Tool" #="{ item }">
                <ToolbarToggleItem
                  v-if="canAnnotate"
                  class="option"
                  value="HighlightAnnotation"
                  :aria-label="item"
                  :class="{ active: currentMouseTool === 'HighlightAnnotation' }"
                >
                  <i class="fas fa-highlighter" />
                </ToolbarToggleItem>
              </StringTooltip>
              <StringTooltip item="Select and Copy Text Tool" #="{ item }">
                <ToolbarToggleItem
                  class="option"
                  value="SelectText"
                  :aria-label="item"
                  :class="{ active: currentMouseTool === 'SelectText' }"
                >
                  <i class="fas fa-italic" />
                </ToolbarToggleItem>
              </StringTooltip>
            </div>
            <div v-if="$can('redact') && !isPopout" class="options-container tw-flex">
              <StringTooltip item="Redact text" #="{ item }">
                <ToolbarToggleItem
                  class="option"
                  value="TextSelectionRedaction"
                  :aria-label="item"
                  :class="{ active: currentMouseTool === 'TextSelectionRedaction' }"
                >
                  <i class="fas fa-eraser" />
                </ToolbarToggleItem>
              </StringTooltip>
              <StringTooltip item="Redact area" #="{ item }">
                <ToolbarToggleItem
                  class="option"
                  value="RectangleRedaction"
                  :aria-label="item"
                  :class="{ active: currentMouseTool === 'RectangleRedaction' }"
                >
                  <div class="tw-mx-1 tw-w-6 tw-rounded-md tw-border tw-p-px *:!tw-m-0">
                    <i aria-hidden="true" class="p-0 fas fa-eraser ![font-size:10px]" />
                  </div>
                </ToolbarToggleItem>
              </StringTooltip>
            </div>
          </ToolbarToggleGroup>
          <div v-if="!isPopout" class="options-container tw-flex">
            <StringTooltip item="Rotate all pages to left" #="{ item }">
              <ToolbarButton class="option" :aria-label="item" @click="rotateAllPages">
                <i aria-hidden="true" class="fas fa-undo" data-fa-transform="rotate-45" />
              </ToolbarButton>
            </StringTooltip>
            <StringTooltip item="Rotate current page to left" #="{ item }">
              <ToolbarButton class="option" :aria-label="item" @click="rotateCurrentPage">
                <div class="tw-mx-1 tw-w-6 tw-rounded-md tw-border tw-p-px *:!tw-m-0">
                  <i aria-hidden="true" class="p-0 fas fa-undo ![font-size:10px]" />
                </div>
              </ToolbarButton>
            </StringTooltip>
          </div>
        </div>
        <div class="tw-flex tw-flex-shrink-0">
          <div class="tw-mr-1" aria-label="Select Page">
            <StringTooltip item="Previous Page" :root="{ disabled: activePage < 2 }" #="{ disabled, item }">
              <button
                type="button"
                class="focus:tw-outline-none disabled:tw-opacity-75"
                :aria-label="item"
                :aria-disabled="disabled"
                @click="disabled ? undefined : changePage(activePage - 1)"
              >
                <i aria-hidden="true" class="fas fa-chevron-left" />
              </button>
            </StringTooltip>
            <input
              type="number"
              class="page-input"
              :min="1"
              :max="totalPages"
              :value="activePage"
              :aria-label="`Currently on page ${activePage}. Change to jump to page.`"
              @change="changePage(($event.target as HTMLInputElement).valueAsNumber)"
            />
            <span> / {{ totalPages }}</span>
            <StringTooltip item="Next Page" :root="{ disabled: activePage >= totalPages }" #="{ disabled, item }">
              <button
                type="button"
                class="focus:tw-outline-none"
                :aria-label="item"
                :aria-disabled="disabled"
                @click="disabled ? undefined : changePage(activePage + 1)"
              >
                <i aria-hidden="true" class="fas fa-chevron-right" />
              </button>
            </StringTooltip>
          </div>
          <div class="options-container tw-mr-2">
            <StringTooltip item="Decrease Scale" #="{ item }">
              <button type="button" class="focus:tw-outline-none" :aria-label="item" @click="changeScale('DECREASE')">
                <i aria-hidden="true" class="fas fa-minus" />
              </button>
            </StringTooltip>
            <span :aria-label="`Current Scale ${scale}`">{{ scale }}</span>
            <StringTooltip item="Increase Scale" #="{ item }">
              <button type="button" class="focus:tw-outline-none" :aria-label="item" @click="changeScale('INCREASE')">
                <i aria-hidden="true" class="fas fa-plus" />
              </button>
            </StringTooltip>
            <StringTooltip v-if="!fitToScreen" item="Fit to Screen" #="{ item }">
              <button type="button" class="active option" :aria-label="item" @click="changeScale('FIT')">
                <i aria-hidden="true" class="fas fa-expand" />
              </button>
            </StringTooltip>
          </div>
        </div>
      </ToolbarRoot>
      <SummaryThemesLegend :file-reference="file.fileReference" />
      <div class="tw-flex tw-flex-1 tw-items-stretch tw-overflow-y-auto">
        <template v-for="panel in viewerPanels" :key="panel.key">
          <component
            :is="panel.panelComponent"
            v-if="panel.showPanel"
            :="panel.panelComponentProps"
            v-on="panel.panelComponentEmits"
          />
        </template>
        <div
          class="tw-shrink-0 tw-grow tw-overflow-auto *:*:tw-min-w-[--page-size]"
          :style="{ '--page-size': fitToScreen ? undefined : `${(scale * viewerScaleWithoutPanels.width) / 100}px` }"
          @mousemove="pan"
          @mouseup="panEnd"
          @mousedown="panStart"
        >
          <VList
            ref="viewerRef"
            v-slot="{ item }: { item: ViewerPage; index: number }"
            :data="pages"
            :overscan="10"
            @scroll="onScroll"
          >
            <RsViewerPageRenderer
              :key="item.pageNumber"
              :current-mouse-tool="currentMouseTool"
              :session="session.viewingSessionId"
              :viewer-scale="viewerScaleWithoutPanels"
              :page-number="item.pageNumber"
              :file-reference="file.fileReference"
              :annotations="annotations.filter((a) => a.parsedMarkData.pageNumber === item.pageNumber)"
              :page-scale="getPageScale(item.pageNumber)"
              :svg="item.svg"
              :page-status="item.status"
              :text-lines="item.textLines"
              :dimensions="item.dimensions ?? viewerScaleWithoutPanels"
              :selected-search="selectedSearch"
              :class="{ 'tw-shadow-dark-gray': activePage === item.pageNumber }"
              :search-results="searchResults.filter((sr) => +sr.pageIndex + 1 === +item.pageNumber)"
              :selected-area="selectedArea?.page.number === item.pageNumber ? selectedArea : undefined"
              @load-page="() => loadPage(item.pageNumber)"
              @select-page="() => changePage(item.pageNumber)"
              @drag-end="currentMouseTool = 'PanAndEdit'"
              @reload-annotations="() => $emit('reloadAnnotations')"
            />
          </VList>
        </div>
      </div>
      <ConfirmAnnotationDelete @annotation-deleted="$emit('reloadAnnotations')" />
    </div>
    <RsLoading
      v-else
      id="viewer-loading"
      class="viewer-loading"
      :message="`Viewer session started. Loading document (Attempt ${retryCount + 1} of ${Math.max(2, retryCount + 1)}).`"
    />
  </Teleport>
</template>
<style lang="scss" scoped>
.viewer-loading {
  width: 100%;
  position: absolute;
  z-index: 1;
}

.viewport {
  position: relative;
}

i {
  font-family: "Font Awesome 5 Free", "Helvetica Neue", Helvetica, Arial, sans-serif;

  span {
    font-weight: 500;
  }
}

.page-input {
  box-shadow: rgba(0, 0, 0, 0.2) 0 1px 1px inset;
  line-height: 12px;
  color: #555555;
  width: 40px;
  border: 0;
  padding-left: 5px;
  vertical-align: text-bottom;
}

.image-tools {
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid #c2c5ce;
  box-shadow: rgba(0, 0, 0, 0.3) 0 2px 2px 0;
  overflow: auto;
  position: absolute;
  width: 200px;
  padding: 8px;
  margin: 0;
  right: -2px;
  top: 25px;
  transition-delay: 0s;
  transition-duration: 0.2s;
  transition-property: height;
  transition-timing-function: ease;
  visibility: visible;
  z-index: 3;
  color: #555555;
}

.drag-slider {
  width: 70%;
  margin-top: 2px;
}

.p-0 {
  padding: 0 !important;
}
</style>
<style>
.vue-recycle-scroller__item-wrapper {
  overflow: initial !important;
}
</style>
